/**
 * Analytics Utility for TrackMe.info
 * Provides easy-to-use functions for tracking user interactions
 */

import api from '../services/api';

// Generate session ID
const generateSessionId = () => {
  if (!window.sessionStorage.getItem('analytics_session_id')) {
    window.sessionStorage.setItem('analytics_session_id', `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  }
  return window.sessionStorage.getItem('analytics_session_id');
};

// Get device information
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  let deviceType = 'desktop';
  let browser = 'unknown';

  // Detect device type
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    deviceType = /iPad/i.test(userAgent) ? 'tablet' : 'mobile';
  }

  // Detect browser
  if (userAgent.includes('Chrome')) browser = 'chrome';
  else if (userAgent.includes('Firefox')) browser = 'firefox';
  else if (userAgent.includes('Safari')) browser = 'safari';
  else if (userAgent.includes('Edge')) browser = 'edge';

  return { deviceType, browser };
};

// Get geographic info (basic)
const getGeoInfo = () => {
  // This would typically use a geolocation service
  // For now, we'll use basic detection
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const language = navigator.language;
  
  return { timezone, language };
};

// Core tracking function
const trackEvent = async (eventType, eventData = {}) => {
  try {
    const sessionId = generateSessionId();
    const deviceInfo = getDeviceInfo();
    const geoInfo = getGeoInfo();
    
    const enrichedData = {
      ...eventData,
      session_id: sessionId,
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer,
      ...deviceInfo,
      ...geoInfo
    };

    await api.trackAnalyticsEvent(eventType, enrichedData);
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

// Specific tracking functions
export const analytics = {
  // Page tracking
  trackPageView: (pagePath = window.location.pathname) => {
    trackEvent('page_view', { page_path: pagePath });
  },

  // User interactions
  trackItemView: (itemId, itemName) => {
    trackEvent('item_view', { item_id: itemId, item_name: itemName });
  },

  trackStoryView: (storyId, itemId, storyContent) => {
    trackEvent('story_view', { 
      story_id: storyId, 
      item_id: itemId, 
      story_content: storyContent?.substring(0, 100) 
    });
  },

  trackQRScan: (itemId, itemName, location) => {
    trackEvent('qr_scan', { 
      item_id: itemId, 
      item_name: itemName, 
      location: location 
    });
  },

  trackItemCreation: (itemId, itemName, creatorId) => {
    trackEvent('item_created', { 
      item_id: itemId, 
      item_name: itemName, 
      user_id: creatorId 
    });
  },

  trackStoryAddition: (storyId, itemId, userId, location) => {
    trackEvent('story_added', { 
      story_id: storyId, 
      item_id: itemId, 
      user_id: userId, 
      location: location 
    });
  },

  trackUserRegistration: (userId, method = 'email') => {
    trackEvent('user_registered', { 
      user_id: userId, 
      registration_method: method 
    });
  },

  trackSearch: (searchTerm, results_count = 0) => {
    trackEvent('search', { 
      search_term: searchTerm, 
      results_count: results_count 
    });
  },

  trackButtonClick: (buttonName, context = '') => {
    trackEvent('button_click', { 
      button_name: buttonName, 
      context: context 
    });
  },

  trackMapInteraction: (action, details = {}) => {
    trackEvent('map_interaction', { 
      action: action, // zoom, pan, marker_click, etc.
      ...details 
    });
  },

  trackFileDownload: (fileName, fileType, context = '') => {
    trackEvent('file_download', { 
      file_name: fileName, 
      file_type: fileType, 
      context: context 
    });
  },

  trackError: (errorType, errorMessage, context = '') => {
    trackEvent('error', { 
      error_type: errorType, 
      error_message: errorMessage, 
      context: context 
    });
  }
};

// Auto-track page views
let currentPath = window.location.pathname;
const trackPageChanges = () => {
  const newPath = window.location.pathname;
  if (newPath !== currentPath) {
    currentPath = newPath;
    analytics.trackPageView(newPath);
  }
};

// Set up automatic page view tracking for SPAs
if (typeof window !== 'undefined') {
  // Initial page view
  analytics.trackPageView();
  
  // Track page changes in SPA
  window.addEventListener('popstate', trackPageChanges);
  
  // Override pushState and replaceState to track programmatic navigation
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;
  
  history.pushState = function(...args) {
    originalPushState.apply(history, args);
    setTimeout(trackPageChanges, 0);
  };
  
  history.replaceState = function(...args) {
    originalReplaceState.apply(history, args);
    setTimeout(trackPageChanges, 0);
  };
}

export default analytics;